import React, { useState } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { unclaimOffer } from '../../../redux/slices/newSellscreenSlice';
import { ClaimableOffer, PricedLine } from '../../../redux/utils/discountEngine';
import { formatMinor } from './discountLabels';
import AutomaticDiscountPicker from './automaticDiscountPicker';
import ClaimOfferSheet from './claimOfferSheet';

// Per-line discount UI: the applied discount, the automatic picker, and "claim offer" chips
const LineDiscountStrip = ({ line, isWrite }: { line: PricedLine, isWrite: boolean }) => {
	const dispatch = useAppDispatch()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [pickerVisible, setPickerVisible] = useState(false)
	const [offer, setOffer] = useState<ClaimableOffer | null>(null)

	const { discount, automaticOptions, claimableOffers } = line
	const canPick = isWrite && automaticOptions.length > 0 && discount?.kind !== "BUY_X_GET_Y"
	if (!discount && !automaticOptions.length && !claimableOffers.length) return null

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.strip}>
						{discount ?
							<View style={[styles.badge, discount.kind === "BUY_X_GET_Y" ? styles.offerBadge : styles.autoBadge]}>
								<ZenIcon type='material-community' name={discount.kind === "BUY_X_GET_Y" ? 'gift-outline' : 'tag-outline'} size={13} color={discount.kind === "BUY_X_GET_Y" ? theme?.blue600 : theme?.tealGreen} />
								<ZenText content={`${discount.title} · -${formatMinor(currencySymbol, discount.amount)}`} style={[styles.badgeText, { color: discount.kind === "BUY_X_GET_Y" ? theme?.blue600 : theme?.tealGreen }]} />
								{discount.kind === "BUY_X_GET_Y" && isWrite ?
									<TouchableOpacity hitSlop={8} onPress={() => dispatch(unclaimOffer({ locationId, discountId: discount.discountId }))}>
										<ZenIcon type='material-community' name='close-circle' size={14} color={theme?.blue600} />
									</TouchableOpacity> : null}
							</View> : null}

						{canPick ?
							<TouchableOpacity style={styles.linkChip} onPress={() => setPickerVisible(true)}>
								<ZenText content={discount ? (automaticOptions.length > 1 ? "Change" : "Remove") : "Apply discount"} style={styles.linkText} />
							</TouchableOpacity> : null}

						{isWrite ? claimableOffers.map((item) => (
							<TouchableOpacity key={item.discountId} style={styles.claimChip} onPress={() => setOffer(item)}>
								<ZenIcon type='material-community' name='gift-outline' size={13} color={theme?.goldenOrange} />
								<ZenText content={`Claim: ${item.title}`} style={styles.claimText} />
							</TouchableOpacity>
						)) : null}

						{canPick ? <AutomaticDiscountPicker visible={pickerVisible} onClose={() => setPickerVisible(false)} line={line} /> : null}
						{offer ? <ClaimOfferSheet visible={Boolean(offer)} onClose={() => setOffer(null)} offer={offer} /> : null}
					</View>
				)
			}}
		</ThemeConsumer>
	)
}

const style = (theme: Theme) => StyleSheet.create({
	strip: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginBottom: 8 },
	badge: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 0.6 },
	autoBadge: { backgroundColor: theme?.lightTealGreen, borderColor: theme?.tealGreen },
	offerBadge: { backgroundColor: theme?.blue50, borderColor: theme?.blue600 },
	badgeText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium },
	linkChip: { paddingHorizontal: 6, paddingVertical: 4 },
	linkText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.bold, color: theme?.primary, textDecorationLine: 'underline' },
	claimChip: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6, borderWidth: 0.6, borderColor: theme?.goldenOrange, backgroundColor: theme?.warmCream },
	claimText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.goldenOrange },
})

export default LineDiscountStrip
